import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Divider,
  Collapse
} from '@mui/material';
import {
  Refresh,
  ArrowBack,
  EmojiEvents,
  Timer,
  ViewList,
  ViewModule,
  TrendingUp,
  TrendingDown,
  Remove,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

export default function LiveScoreboard() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [viewMode, setViewMode] = useState('liveScore'); // 'leaderboard', 'detailed', or 'liveScore'
  const [courseInfoExpanded, setCourseInfoExpanded] = useState(false);

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchData(true); // Silent refresh
      }, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, tournamentId]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      // Fetch tournament data
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      // Fetch all players in the tournament
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPlayers(playersData);

      // Calculate leaderboard
      const leaderboardData = calculateLeaderboard(tournamentData, playersData);
      setLeaderboard(leaderboardData);
      setLastUpdated(new Date());

    } catch (error) {
      console.error('Error fetching scoreboard data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const calculateLeaderboard = (tournament, players) => {
    const scores = tournament.scores || {};
    const tournamentType = tournament.type || 'STROKEPLAY_SCRATCH';

    // Get players who have scores
    const playersWithScores = players
      .filter(player => scores[player.id])
      .map(player => {
        const playerScores = scores[player.id];
        const holes = Object.keys(playerScores).length;
        const grossTotal = Object.values(playerScores).reduce((sum, score) => sum + (parseInt(score) || 0), 0);
        
        // Calculate net score based on tournament type
        let netTotal = grossTotal;
        if (tournamentType === 'STROKEPLAY_HANDICAPPED' && player.handicap) {
          netTotal = grossTotal - Math.floor(player.handicap);
        }

        // Calculate score relative to par (based on holes played)
        const coursePar = tournament.golfCourse?.totalPar || 72;
        const parPerHole = coursePar / 18;
        const expectedPar = Math.round(parPerHole * holes);
        const scoringScore = tournamentType === 'STROKEPLAY_SCRATCH' ? grossTotal : netTotal;
        const scoreToPar = scoringScore - expectedPar;

        // Calculate front 9 and back 9
        const front9 = [1, 2, 3, 4, 5, 6, 7, 8, 9].reduce((sum, hole) => 
          sum + (parseInt(playerScores[hole]) || 0), 0);
        const back9 = [10, 11, 12, 13, 14, 15, 16, 17, 18].reduce((sum, hole) => 
          sum + (parseInt(playerScores[hole]) || 0), 0);

        return {
          id: player.id,
          name: `${player.firstName} ${player.lastName}`,
          handicap: player.handicap || 0,
          holesPlayed: holes,
          grossTotal,
          netTotal,
          scoringScore, // The score used for sorting
          scoreToPar,
          front9: front9 || null,
          back9: back9 || null,
          scores: playerScores,
          lastUpdated: tournament.lastUpdated?.[player.id]
        };
      });

    // Sort by scoring score only (ignore holes played)
    playersWithScores.sort((a, b) => {
      return a.scoringScore - b.scoringScore;
    });

    // Assign positions with tie handling
    let currentPosition = 1;
    let previousScore = null;
    let playersAtCurrentScore = 0;

    return playersWithScores.map((player, index) => {
      if (previousScore === null || player.scoringScore !== previousScore) {
        // New score, update position
        currentPosition = index + 1;
        playersAtCurrentScore = 1;
        previousScore = player.scoringScore;
      } else {
        // Same score as previous player (tied)
        playersAtCurrentScore++;
      }

      return {
        ...player,
        position: currentPosition,
        isTied: playersAtCurrentScore > 1 || 
                (index < playersWithScores.length - 1 && 
                 playersWithScores[index + 1].scoringScore === player.scoringScore),
        previousPosition: player.previousPosition || currentPosition
      };
    });
  };

  const getPositionChange = (current, previous) => {
    if (current < previous) {
      return <TrendingUp color="success" />;
    } else if (current > previous) {
      return <TrendingDown color="error" />;
    }
    return <Remove color="disabled" />;
  };

  const formatScoreToPar = (score) => {
    if (score === 0) return 'E';
    if (score > 0) return `+${score}`;
    return score.toString();
  };

  const getScoreColor = (scoreToPar) => {
    if (scoreToPar < 0) return 'success';
    if (scoreToPar === 0) return 'info';
    return 'error';
  };

  const getHoleScoreStyle = (score, holePar) => {
    if (!score || !holePar) return { bgcolor: 'white', color: 'black' };
    
    const diff = score - holePar;
    
    // Hole in one (ace)
    if (score === 1) {
      return { 
        bgcolor: 'black', 
        color: 'yellow',
        fontWeight: 'bold',
        border: '2px solid gold'
      };
    }
    
    // Eagle (2 under par)
    if (diff === -2) {
      return { 
        bgcolor: 'purple', 
        color: 'white',
        fontWeight: 'bold'
      };
    }
    
    // Birdie (1 under par)
    if (diff === -1) {
      return { 
        bgcolor: 'red', 
        color: 'white',
        fontWeight: 'bold'
      };
    }
    
    // Par
    if (diff === 0) {
      return { 
        bgcolor: 'white', 
        color: 'black',
        border: '1px solid #ddd'
      };
    }
    
    // Bogey (1 over par)
    if (diff === 1) {
      return { 
        bgcolor: 'black', 
        color: 'white',
        fontWeight: 'bold'
      };
    }
    
    // Double bogey or worse (2+ over par)
    return { 
      bgcolor: 'black', 
      color: 'white',
      fontWeight: 'bold',
      border: '2px solid red'
    };
  };

  const renderLeaderboardView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="center" width="80px"><strong>Pos</strong></TableCell>
            <TableCell><strong>Player</strong></TableCell>
            {tournament?.type !== 'STROKEPLAY_SCRATCH' && (
              <TableCell align="center"><strong>HCP</strong></TableCell>
            )}
            <TableCell align="center"><strong>Holes</strong></TableCell>
            <TableCell align="center"><strong>Score</strong></TableCell>
            <TableCell align="center"><strong>Total</strong></TableCell>
            <TableCell align="center"><strong>Thru</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leaderboard.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography color="text.secondary">No scores recorded yet</Typography>
              </TableCell>
            </TableRow>
          ) : (
            leaderboard.map((player, index) => (
              <TableRow 
                key={player.id}
                sx={{ 
                  backgroundColor: index === 0 ? 'rgba(255, 215, 0, 0.1)' : 'inherit',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    {index === 0 && !player.isTied && <EmojiEvents sx={{ color: 'gold', fontSize: 20 }} />}
                    <Typography variant="h6" fontWeight={index < 3 ? 'bold' : 'normal'}>
                      {player.isTied ? 'T' : ''}{player.position}
                    </Typography>
                    <Box sx={{ width: 20 }}>
                      {getPositionChange(player.position, player.previousPosition)}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                    {player.name}
                  </Typography>
                </TableCell>
                {tournament?.type !== 'STROKEPLAY_SCRATCH' && (
                  <TableCell align="center">{player.handicap}</TableCell>
                )}
                <TableCell align="center">{player.holesPlayed}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={formatScoreToPar(player.scoreToPar)}
                    color={getScoreColor(player.scoreToPar)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="h6" fontWeight={index < 3 ? 'bold' : 'normal'}>
                      {player.scoringScore}
                    </Typography>
                    {player.holesPlayed < 18 && (
                      <Typography variant="caption" color="text.secondary">
                        (thru {player.holesPlayed})
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography color="text.secondary">
                    {player.holesPlayed === 18 ? 'F' : player.holesPlayed}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderDetailedView = () => (
    <Grid container spacing={2}>
      {leaderboard.map((player, index) => (
        <Grid item xs={12} key={player.id}>
          <Card variant="outlined" sx={{ 
            backgroundColor: index === 0 ? 'rgba(255, 215, 0, 0.1)' : 'inherit'
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h5" fontWeight="bold">
                    {player.isTied ? 'T' : ''}#{player.position}
                  </Typography>
                  {index === 0 && !player.isTied && <EmojiEvents sx={{ color: 'gold', fontSize: 30 }} />}
                  <Box>
                    <Typography variant="h6">{player.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Handicap: {player.handicap}
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h4" fontWeight="bold">
                    {player.scoringScore}
                  </Typography>
                  <Chip
                    label={formatScoreToPar(player.scoreToPar)}
                    color={getScoreColor(player.scoreToPar)}
                    size="small"
                  />
                  {player.holesPlayed < 18 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Through {player.holesPlayed} holes
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Front 9</Typography>
                  <Typography variant="h6">{player.front9 || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Back 9</Typography>
                  <Typography variant="h6">{player.back9 || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Holes Played</Typography>
                  <Typography variant="h6">{player.holesPlayed} / 18</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Typography variant="h6">
                    {player.holesPlayed === 18 ? 'Complete' : `Thru ${player.holesPlayed}`}
                  </Typography>
                </Grid>
              </Grid>

              {/* Hole-by-hole scores */}
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Hole-by-Hole
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {Array.from({ length: 18 }, (_, i) => i + 1).map(hole => {
                    const score = player.scores[hole];
                    const holePar = tournament.golfCourse?.holes?.[hole - 1]?.par || 4;
                    const scoreStyle = score ? getHoleScoreStyle(parseInt(score), holePar) : {};
                    
                    return (
                      <Chip
                        key={hole}
                        label={score || '-'}
                        size="small"
                        variant={score ? 'filled' : 'outlined'}
                        sx={score ? {
                          bgcolor: scoreStyle.bgcolor,
                          color: scoreStyle.color,
                          fontWeight: scoreStyle.fontWeight,
                          border: scoreStyle.border,
                          '& .MuiChip-label': {
                            color: scoreStyle.color
                          }
                        } : {}}
                      />
                    );
                  })}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderLiveScoreView = () => (
    <Box>
      {/* Combined Table with Par Row and Player Rows */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            {/* Hole Numbers Row */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '200px', minWidth: '180px' }}>Player</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '80px', minWidth: '70px' }} align="center">Score</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '70px', minWidth: '60px' }} align="center">To Par</TableCell>
              {Array.from({ length: 18 }, (_, i) => i + 1).map(hole => (
                <TableCell key={hole} align="center" sx={{ 
                  fontWeight: 'bold', 
                  minWidth: '40px',
                  width: '40px',
                  bgcolor: 'primary.light',
                  color: 'white',
                  fontSize: '0.8rem',
                  px: 0.5
                }}>
                  {hole}
                </TableCell>
              ))}
            </TableRow>
            {/* Par Row */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Par</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100' }} />
              <TableCell sx={{ bgcolor: 'grey.100' }} />
              {Array.from({ length: 18 }, (_, i) => i + 1).map(hole => {
                const holePar = tournament.golfCourse?.holes?.[hole - 1]?.par || 4;
                return (
                  <TableCell key={hole} align="center" sx={{ 
                    fontWeight: 'bold',
                    bgcolor: 'grey.100',
                    fontSize: '0.8rem',
                    minWidth: '40px',
                    width: '40px',
                    px: 0.5
                  }}>
                    {holePar}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.length === 0 ? (
              <TableRow>
                <TableCell colSpan={21} align="center">
                  <Typography color="text.secondary">No scores recorded yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              leaderboard.map((player, index) => (
                <TableRow 
                  key={player.id}
                  sx={{ 
                    backgroundColor: index === 0 ? 'rgba(255, 215, 0, 0.1)' : 'inherit',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <TableCell sx={{ fontWeight: index < 3 ? 'bold' : 'normal', width: '200px', minWidth: '180px' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {index === 0 && !player.isTied && <EmojiEvents sx={{ color: 'gold', fontSize: 16 }} />}
                      <Typography variant="body2" fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {player.isTied ? 'T' : ''}{player.position}. {player.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: '80px', minWidth: '70px' }}>
                    <Typography variant="body2" fontWeight={index < 3 ? 'bold' : 'normal'}>
                      {player.scoringScore}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ width: '70px', minWidth: '60px' }}>
                    <Chip
                      label={formatScoreToPar(player.scoreToPar)}
                      color={getScoreColor(player.scoreToPar)}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  </TableCell>
                  {Array.from({ length: 18 }, (_, i) => i + 1).map(hole => {
                    const score = player.scores[hole];
                    const holePar = tournament.golfCourse?.holes?.[hole - 1]?.par || 4;
                    const scoreStyle = score ? getHoleScoreStyle(parseInt(score), holePar) : {};
                    
                    return (
                      <TableCell key={hole} align="center" sx={{ 
                        minWidth: '40px',
                        width: '40px',
                        px: 0.5
                      }}>
                        {score ? (
                          <Box
                            sx={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              color: scoreStyle.color,
                              bgcolor: scoreStyle.bgcolor,
                              border: scoreStyle.border,
                              mx: 'auto'
                            }}
                          >
                            {score}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                              bgcolor: 'grey.100',
                              border: '1px solid #ddd',
                              mx: 'auto'
                            }}
                          >
                            -
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Score Legend */}
      <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>Scoring Legend:</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Chip label="Hole in One" size="small" sx={{ bgcolor: 'black', color: 'yellow', fontWeight: 'bold', border: '2px solid gold' }} />
          <Chip label="Eagle" size="small" sx={{ bgcolor: 'purple', color: 'white', fontWeight: 'bold' }} />
          <Chip label="Birdie" size="small" sx={{ bgcolor: 'red', color: 'white', fontWeight: 'bold' }} />
          <Chip label="Par" size="small" sx={{ bgcolor: 'white', color: 'black', border: '1px solid #ddd' }} />
          <Chip label="Bogey" size="small" sx={{ bgcolor: 'black', color: 'white', fontWeight: 'bold' }} />
          <Chip label="Double+" size="small" sx={{ bgcolor: 'black', color: 'white', fontWeight: 'bold', border: '2px solid red' }} />
        </Box>
      </Paper>
    </Box>
  );

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(`/tournaments/${tournamentId}/director`)}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4">{tournament.name}</Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Live Scoreboard
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="leaderboard">
              <ViewList sx={{ mr: 1 }} /> Leaderboard
            </ToggleButton>
            <ToggleButton value="detailed">
              <ViewModule sx={{ mr: 1 }} /> Detailed
            </ToggleButton>
            <ToggleButton value="liveScore">
              <TrendingUp sx={{ mr: 1 }} /> Live Score
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => fetchData()} color="primary">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Tournament Info */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Status</Typography>
              <Chip 
                label={tournament.status?.toUpperCase() || 'DRAFT'} 
                color={tournament.status === 'active' ? 'success' : 'default'}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Course</Typography>
              <Typography variant="h6">
                {tournament.golfCourse?.name || 'Not set'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Players</Typography>
              <Typography variant="h6">
                {leaderboard.length} / {tournament.maxPlayers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Timer fontSize="small" color="action" />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {lastUpdated.toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Auto-refresh toggle */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Chip
          icon={<Refresh />}
          label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          onClick={() => setAutoRefresh(!autoRefresh)}
          color={autoRefresh ? 'success' : 'default'}
          variant={autoRefresh ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Course Information for Detailed View */}
      {viewMode === 'detailed' && tournament.golfCourse && (
        <>
          {/* Hole Pars and Index */}
          <Paper sx={{ mb: 2 }}>
            <Box 
              sx={{ 
                p: 1, 
                px: 1.5,
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setCourseInfoExpanded(!courseInfoExpanded)}
            >
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
                Course: {tournament.golfCourse.name} (Par {tournament.golfCourse.totalPar || 72})
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {courseInfoExpanded ? 'Hide' : 'Show'} Details
                </Typography>
                {courseInfoExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </Box>
            </Box>
            
            <Collapse in={courseInfoExpanded}>
              <Box sx={{ p: 1, px: 1.5, pt: 0, overflowX: 'auto' }}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.5, fontSize: '0.7rem' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', fontSize: '0.7rem' }}>Hole</TableCell>
                    {Array.from({ length: 9 }, (_, i) => i + 1).map(hole => (
                      <TableCell key={hole} align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.light', fontSize: '0.7rem' }}>
                        {hole}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'warning.light', fontSize: '0.7rem' }}>Out</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Par</TableCell>
                    {Array.from({ length: 9 }, (_, i) => i + 1).map(hole => (
                      <TableCell key={hole} align="center" sx={{ fontSize: '0.7rem' }}>
                        {tournament.golfCourse.holes?.[hole - 1]?.par || 4}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100', fontSize: '0.7rem' }}>
                      {Array.from({ length: 9 }, (_, i) => 
                        tournament.golfCourse.holes?.[i]?.par || 4
                      ).reduce((sum, par) => sum + par, 0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}>Index</TableCell>
                    {Array.from({ length: 9 }, (_, i) => i + 1).map(hole => (
                      <TableCell key={hole} align="center" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        {tournament.golfCourse.holes?.[hole - 1]?.strokeIndex || hole}
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>

              <Table size="small" sx={{ mt: 1, '& .MuiTableCell-root': { py: 0.25, px: 0.5, fontSize: '0.7rem' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', fontSize: '0.7rem' }}>Hole</TableCell>
                    {Array.from({ length: 9 }, (_, i) => i + 10).map(hole => (
                      <TableCell key={hole} align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.light', fontSize: '0.7rem' }}>
                        {hole}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'warning.light', fontSize: '0.7rem' }}>In</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'success.light', fontSize: '0.7rem' }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Par</TableCell>
                    {Array.from({ length: 9 }, (_, i) => i + 10).map(hole => (
                      <TableCell key={hole} align="center" sx={{ fontSize: '0.7rem' }}>
                        {tournament.golfCourse.holes?.[hole - 1]?.par || 4}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100', fontSize: '0.7rem' }}>
                      {Array.from({ length: 9 }, (_, i) => 
                        tournament.golfCourse.holes?.[i + 9]?.par || 4
                      ).reduce((sum, par) => sum + par, 0)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.200', fontSize: '0.7rem' }}>
                      {tournament.golfCourse.totalPar || 72}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}>Index</TableCell>
                    {Array.from({ length: 9 }, (_, i) => i + 10).map(hole => (
                      <TableCell key={hole} align="center" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        {tournament.golfCourse.holes?.[hole - 1]?.strokeIndex || hole}
                      </TableCell>
                    ))}
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
              </Box>
            </Collapse>
          </Paper>

          {/* Score Legend */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>Scoring Legend:</Typography>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Chip label="Hole in One" size="small" sx={{ bgcolor: 'black', color: 'yellow', fontWeight: 'bold', border: '2px solid gold' }} />
              <Chip label="Eagle" size="small" sx={{ bgcolor: 'purple', color: 'white', fontWeight: 'bold' }} />
              <Chip label="Birdie" size="small" sx={{ bgcolor: 'red', color: 'white', fontWeight: 'bold' }} />
              <Chip label="Par" size="small" sx={{ bgcolor: 'white', color: 'black', border: '1px solid #ddd' }} />
              <Chip label="Bogey" size="small" sx={{ bgcolor: 'black', color: 'white', fontWeight: 'bold' }} />
              <Chip label="Double+" size="small" sx={{ bgcolor: 'black', color: 'white', fontWeight: 'bold', border: '2px solid red' }} />
            </Box>
          </Paper>
        </>
      )}

      {/* Scoreboard */}
      {viewMode === 'leaderboard' ? renderLeaderboardView() : 
       viewMode === 'detailed' ? renderDetailedView() : 
       renderLiveScoreView()}
    </Container>
  );
}

